import type { OnboardingOp, OnboardingState, TourProgressEntry } from "./onboardingTypes";

const cloneState = (state: OnboardingState): OnboardingState =>
  JSON.parse(JSON.stringify(state)) as OnboardingState;

const applyOp = (state: OnboardingState, op: OnboardingOp, nowIso: string): OnboardingState => {
  switch (op.op) {
    case "welcomeSeen": {
      if (state.welcome.seenAt === null) {
        state.welcome = { seenAt: nowIso };
      }
      return state;
    }
    case "tourProgress": {
      const existing = state.tours[op.id];
      if (existing && existing.status === "completed" && op.status !== "completed" && !op.replay) {
        return state;
      }
      const entry: TourProgressEntry = { status: op.status, step: op.step, updatedAt: nowIso };
      if (op.route && (op.status === "started" || op.status === "paused")) {
        entry.route = op.route;
      }
      state.tours = { ...state.tours, [op.id]: entry };
      return state;
    }
    case "tourClear": {
      const tours = { ...state.tours };
      delete tours[op.id];
      state.tours = tours;
      return state;
    }
    case "checklistItemDone": {
      if (!state.checklist.items[op.id]) {
        state.checklist = {
          ...state.checklist,
          items: { ...state.checklist.items, [op.id]: { doneAt: nowIso } },
        };
      }
      return state;
    }
    case "checklistDismiss": {
      state.checklist = { ...state.checklist, dismissedAt: nowIso };
      return state;
    }
    case "checklistRestore": {
      state.checklist = { ...state.checklist, dismissedAt: null };
      return state;
    }
    case "checklistLauncherSeen": {
      if (!state.checklist.launcherSeenAt) {
        state.checklist = { ...state.checklist, launcherSeenAt: nowIso };
      }
      return state;
    }
    case "relaunchAnswered": {
      if (state.relaunchPrompt && state.relaunchPrompt.answeredAt === null) {
        state.relaunchPrompt = { answeredAt: nowIso };
      }
      return state;
    }
    case "suggestionsMute": {
      if (!state.suggestions?.mutedAt) {
        state.suggestions = { mutedAt: nowIso };
      }
      return state;
    }
    case "suggestionsUnmute": {
      state.suggestions = { mutedAt: null };
      return state;
    }
    default:
      return state;
  }
};

export const applyOps = (
  state: OnboardingState,
  ops: OnboardingOp[],
  nowIso: string,
): OnboardingState => {
  let next = cloneState(state);

  for (const op of ops) {
    next = applyOp(next, op, nowIso);
  }

  return next;
};

export default applyOps;
